export const WORKFORCE_BASE_PATH = "/workforce";

/**
 * True when the pathname belongs to the Workforce Lifecycle Management module.
 * Segment-bounded: "/workforce", "/workforce/...", "/workforce?..." match;
 * "/workforcex" does not.
 */
export function isWorkforcePath(pathname: string): boolean {
  return (
    pathname === WORKFORCE_BASE_PATH ||
    pathname.startsWith(WORKFORCE_BASE_PATH + "/") ||
    pathname.startsWith(WORKFORCE_BASE_PATH + "?")
  );
}

/**
 * Routes that belong to no product — the shell hosts one copy of them and
 * either module can open them. `/account` is the case that prompted this
 * (#1046): it lives outside `/workforce`, so deriving the active product from
 * the path alone silently flipped the header pill and the whole sidebar to
 * Service Desk when a Workforce user opened "View profile".
 *
 * skyAgent and skyBrain are here for the same reason, and because the agent is
 * genuinely one agent: it is instance-wide, its tools are gated on the platform
 * role rather than on a product, and it now carries workforce tools alongside
 * the service-desk ones. Hosting a second copy under /workforce would mean two
 * workspaces, two conversation histories and two sets of settings for one
 * thing.
 */
// /users — Users & access — is shared by all three modules (shared user management).
const MODULE_NEUTRAL_PATHS = ["/account", "/skyagent", "/skybrain", "/users"];

export function isModuleNeutralPath(pathname: string): boolean {
  return MODULE_NEUTRAL_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

/**
 * Is the shell currently showing Workforce Lifecycle?
 *
 * A module-neutral route keeps whichever module you arrived from, carried in
 * `?from=workforce` by the link that navigated there. Without that the module
 * is read from the path, so a direct hit on /account (bookmark, refresh, fresh
 * tab) still resolves sensibly rather than depending on remembered state.
 */
export function isWorkforceContext(pathname: string, search?: string): boolean {
  if (isWorkforcePath(pathname)) return true;
  if (!isModuleNeutralPath(pathname)) return false;
  return new URLSearchParams(search ?? "").get("from") === "workforce";
}
